import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { PencilSimple, X } from "@phosphor-icons/react";
import { api, fmtMoney, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

export function Breakdown({ p, canAdjust, onRemoveAdjustment }) {
  return (
    <div className="space-y-2.5 text-sm">
      <div className="flex justify-between"><span className="text-muted-foreground">Pay period</span><span>{fmtDate(p.period_start)} → {fmtDate(p.period_end)}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Pay date</span><span>{fmtDate(p.pay_date)}</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Base pay ({p.pay_type}, {p.pay_schedule}){p.prorated ? " — pro-rated across a pay change" : ""}</span><span>{fmtMoney(p.base_pay)}</span></div>
      {p.prorated && (p.segments || []).map((s, i) => (
        <div key={i} className="flex justify-between text-xs text-muted-foreground pl-4" data-testid={`base-segment-${i}`}>
          <span>{fmtDate(s.start)} → {fmtDate(s.end)} @ {s.pay_type === "salary" ? `${fmtMoney(s.annual_salary)}/yr` : `${fmtMoney(s.hourly_rate)}/hr`}</span>
          <span>{fmtMoney(s.base)}</span>
        </div>
      ))}
      <div className="flex justify-between"><span className="text-muted-foreground">PTO hours (paid)</span><span>{p.pto_hours_paid}h</span></div>
      <div className="flex justify-between"><span className="text-muted-foreground">Sick hours (paid, note approved)</span><span>{p.sick_hours_paid}h</span></div>
      {(p.sick_hours_unpaid > 0 || p.other_unpaid_hours > 0) && (
        <div className="flex justify-between text-red-600">
          <span>Unpaid ({p.sick_hours_unpaid}h sick w/o note + {p.other_unpaid_hours}h other)</span>
          <span>−{fmtMoney(p.unpaid_deduction)}</span>
        </div>
      )}
      {(p.adjustments || []).map((a) => (
        <div key={a.id} className={`flex justify-between items-center gap-2 ${a.amount > 0 ? "text-emerald-700" : "text-red-600"}`} data-testid={`adjustment-line-${a.id}`}>
          <span className="min-w-0">Adjustment: "{a.reason}" — {a.created_by_name}</span>
          <span className="flex items-center gap-1.5 shrink-0">
            {a.amount > 0 ? "+" : "−"}{fmtMoney(Math.abs(a.amount))}
            {canAdjust && (
              <button data-testid={`remove-adjustment-${a.id}`} title="Remove adjustment" onClick={() => onRemoveAdjustment(a)}
                className="text-muted-foreground hover:text-red-600 transition-colors"><X size={13} /></button>
            )}
          </span>
        </div>
      ))}
      <div className="flex justify-between border-t border-border pt-2.5 font-semibold text-base">
        <span>Gross estimate</span><span className="text-indigo-700">{fmtMoney(p.gross_estimate)}</span>
      </div>
      {p.sick_details?.map((s, i) => (
        <p key={i} className="text-xs text-muted-foreground">
          Sick leave {s.hours}h — {s.paid ? "paid (doctor's note approved)" : `unpaid (note ${s.note_status})`}
        </p>
      ))}
      <p className="text-xs text-muted-foreground pt-2">Gross estimate before taxes and withholdings. If you are owed commissions, they are <strong>not reflected</strong> in this estimate.</p>
    </div>
  );
}

export default function PayrollPage() {
  const { user, hasPerm } = useAuth();
  const [own, setOwn] = useState(null);
  const [overview, setOverview] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [adjustFor, setAdjustFor] = useState(null);
  const [adjForm, setAdjForm] = useState({ amount: "", reason: "", period_start: "", period_end: "" });
  const [busy, setBusy] = useState(false);
  const canViewAll = hasPerm("pay.view");
  const canAdjust = hasPerm("pay.adjust");

  const load = useCallback(() => {
    api.get(`/payroll/next-paycheck/${user.id}`).then((r) => setOwn(r.data)).catch(() => {});
    if (canViewAll) api.get("/payroll/overview").then((r) => setOverview(r.data)).catch(() => {});
  }, [user.id]);
  useEffect(() => { load(); }, [load]);

  const openAdjust = (p) => {
    setAdjustFor(p);
    setAdjForm({ amount: "", reason: "", period_start: p.period_start, period_end: p.period_end });
  };

  const submitAdjustment = async () => {
    setBusy(true);
    try {
      await api.post("/payroll/adjustments", {
        user_id: adjustFor.user_id,
        amount: parseFloat(adjForm.amount),
        period_start: adjForm.period_start,
        period_end: adjForm.period_end,
        reason: adjForm.reason,
      });
      toast.success("Payroll adjustment recorded — logged to audit trail");
      setAdjustFor(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not record adjustment");
    } finally {
      setBusy(false);
    }
  };

  const removeAdjustment = async (a) => {
    try {
      await api.delete(`/payroll/adjustments/${a.id}`);
      toast.success("Adjustment removed — logged to audit trail");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove adjustment");
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <p className="uppercase tracking-[0.2em] text-xs font-semibold text-indigo-600 mb-2">Compensation</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight">Payroll</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-xl">Next paycheck auto-calculated from wage parameters, approved PTO, sick days and doctor's note status.</p>
      </div>

      <div className="bg-card border border-border rounded-md p-6" data-testid="own-paycheck-card">
        <h3 className="font-heading font-semibold tracking-tight mb-4">My next paycheck</h3>
        {own ? <Breakdown p={own} canAdjust={canAdjust} onRemoveAdjustment={removeAdjustment} /> : <p className="text-sm text-muted-foreground">Calculating…</p>}
      </div>

      {canViewAll && (
        <div className="bg-card border border-border rounded-md">
          <div className="px-6 py-4 border-b border-border"><h3 className="font-heading font-semibold tracking-tight">Team payroll overview</h3></div>
          <div className="divide-y divide-border" data-testid="payroll-overview">
            {overview.map((p) => (
              <div key={p.user_id}>
                <button onClick={() => setExpanded(expanded === p.user_id ? null : p.user_id)}
                  data-testid={`payroll-row-${p.user_id}`}
                  className="w-full px-4 sm:px-6 py-3.5 flex items-center justify-between gap-3 hover:bg-muted transition-colors text-left">
                  <div>
                    <p className="text-sm font-medium">{p.full_name}</p>
                    <p className="text-xs text-muted-foreground">{p.pay_type} · {p.pay_schedule} · pay date {fmtDate(p.pay_date)}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    {p.adjustments?.length > 0 && <Badge className={p.adjustments_total >= 0 ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-700"}>adjusted</Badge>}
                    {(p.sick_hours_unpaid > 0 || p.other_unpaid_hours > 0) && <Badge className="bg-red-100 text-red-700">deduction</Badge>}
                    <span className="font-heading font-semibold">{fmtMoney(p.gross_estimate)}</span>
                  </div>
                </button>
                {expanded === p.user_id && (
                  <div className="px-4 sm:px-6 pb-5 pt-1 bg-muted/50 space-y-3">
                    <Breakdown p={p} canAdjust={canAdjust} onRemoveAdjustment={removeAdjustment} />
                    {canAdjust && (
                      <Button data-testid={`adjust-pay-btn-${p.user_id}`} size="sm" variant="outline" onClick={() => openAdjust(p)}>
                        <PencilSimple size={14} className="mr-1.5" /> Adjust this paycheck
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!adjustFor} onOpenChange={() => setAdjustFor(null)}>
        <DialogContent data-testid="adjust-pay-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Adjust {adjustFor?.full_name}'s paycheck</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount ($) <span className="text-red-500">*</span></Label>
              <Input data-testid="adjust-amount-input" type="number" step="0.01" className="mt-1.5" placeholder="e.g. 150 or -75.50"
                value={adjForm.amount} onChange={(e) => setAdjForm({ ...adjForm, amount: e.target.value })} />
              <p className="text-xs text-muted-foreground mt-1">Positive adds to the paycheck, negative deducts.</p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Period start</Label><Input data-testid="adjust-period-start" type="date" className="mt-1.5" value={adjForm.period_start} onChange={(e) => setAdjForm({ ...adjForm, period_start: e.target.value })} /></div>
              <div><Label>Period end</Label><Input data-testid="adjust-period-end" type="date" className="mt-1.5" value={adjForm.period_end} onChange={(e) => setAdjForm({ ...adjForm, period_end: e.target.value })} /></div>
            </div>
            <p className="text-xs text-muted-foreground -mt-2">Pre-filled with the current pay period — the adjustment lands on the paycheck whose period contains the start date.</p>
            <div>
              <Label>Reason <span className="text-red-500">*</span></Label>
              <Textarea data-testid="adjust-reason-input" className="mt-1.5" rows={2} placeholder="e.g. Commission payout for July, correction for missed hours…"
                value={adjForm.reason} onChange={(e) => setAdjForm({ ...adjForm, reason: e.target.value })} />
              <p className="text-xs text-muted-foreground mt-1">Required — recorded permanently in the audit trail and history.</p>
            </div>
            <Button data-testid="confirm-adjustment-btn" onClick={submitAdjustment}
              disabled={busy || !parseFloat(adjForm.amount) || !adjForm.reason.trim() || !adjForm.period_start || !adjForm.period_end}
              className="w-full bg-[#0A0F1C] hover:bg-indigo-600 text-white transition-colors">
              {busy ? "Saving…" : "Record adjustment"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
